import { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Download, Gift } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import FileUpload from "@/components/FileUpload";

const CATEGORIES = ["Primary TET", "Upper Primary TET", "C-TET", "Group C", "Group D", "SSC", "Banking", "General"];

export default function AdminFreeEbooks() {
  const [books, setBooks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", image_url: "", category: "General",
    pages: "", author: "", file_format: "PDF", file_size: "", file_url: ""
  });

  useEffect(() => { fetchBooks(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBooks = async () => {
    const { data } = await api.get("/free-ebooks");
    setBooks(data);
  };

  const resetForm = () => ({
    title: "", description: "", image_url: "", category: "General",
    pages: "", author: "", file_format: "PDF", file_size: "", file_url: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.file_url) return;
    if (editing) {
      await api.put(`/free-ebooks/${editing}`, form);
    } else {
      await api.post("/free-ebooks", form);
    }
    setShowForm(false);
    setEditing(null);
    setForm(resetForm());
    fetchBooks();
  };

  const handleEdit = (book) => {
    setForm({
      title: book.title, description: book.description || "", image_url: book.image_url || "",
      category: book.category || "General", pages: book.pages || "", author: book.author || "",
      file_format: book.file_format || "PDF", file_size: book.file_size || "", file_url: book.file_url || ""
    });
    setEditing(book.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this free e-book?")) {
      await api.delete(`/free-ebooks/${id}`);
      fetchBooks();
    }
  };

  const inputClass = "w-full admin-input border-b-2 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <AdminLayout>
      <div data-testid="admin-free-ebooks-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black admin-text flex items-center gap-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <Gift size={24} className="text-[#059669]" />
              Free E-Books
            </h1>
            <p className="text-sm admin-text-secondary mt-0.5">Upload free PDF books for students to download instantly</p>
          </div>
          <button
            data-testid="add-free-ebook-btn"
            onClick={() => { setShowForm(!showForm); setEditing(null); setForm(resetForm()); }}
            className="bg-[#059669] text-white font-bold py-2.5 px-4 text-xs flex items-center gap-1.5 transition-none"
          >
            <Plus size={14} /> Add Free E-Book
          </button>
        </div>

        {/* Guide */}
        {!showForm && books.length === 0 && (
          <div className="admin-card border p-5 mb-6">
            <h3 className="text-sm font-bold admin-text mb-3">How to add Free E-Books:</h3>
            <ol className="text-xs admin-text-secondary space-y-2 list-decimal list-inside">
              <li>Click <strong>"Add Free E-Book"</strong></li>
              <li>Fill in the book details (title, author, category, etc.)</li>
              <li><strong>Upload the PDF file</strong> using the "Choose File" button OR paste a Google Drive / Dropbox download link</li>
              <li>Save - Students can immediately download from the <strong>Free E-Books</strong> page</li>
            </ol>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="admin-card border p-5 mb-6 space-y-4" data-testid="free-ebook-form">
            <div className="bg-green-50 border border-green-200 p-3 text-center">
              <Gift size={20} className="text-green-600 mx-auto mb-1" />
              <p className="text-xs font-bold text-green-700">This e-book will be FREE for all students</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Book Title</label>
                <input data-testid="feb-title" placeholder="e.g., English Vocabulary 500 Words PDF" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} required />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Author</label>
                <input placeholder="Author name" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Category</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputClass}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Pages</label>
                <input placeholder="e.g., 50 pages" value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Format</label>
                <select value={form.file_format} onChange={(e) => setForm({ ...form, file_format: e.target.value })} className={inputClass}>
                  <option value="PDF">PDF</option>
                  <option value="EPUB">EPUB</option>
                  <option value="DOCX">DOCX</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">File Size</label>
                <input placeholder="e.g., 5 MB" value={form.file_size} onChange={(e) => setForm({ ...form, file_size: e.target.value })} className={inputClass} />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Description</label>
              <textarea placeholder="Brief description of the book..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputClass} h-16`} />
            </div>

            <FileUpload
              label="Book Cover Image"
              accept="image/*"
              uploadType="free-ebooks"
              currentUrl={form.image_url}
              onUpload={(url) => setForm({ ...form, image_url: url })}
            />

            {/* PDF Upload - Most Important */}
            <div className="bg-green-50 border border-green-200 p-4">
              <label className="text-xs font-bold text-green-700 flex items-center gap-1.5 mb-2">
                <Download size={14} /> Upload PDF File (Students will download this)
              </label>
              <FileUpload
                label=""
                accept=".pdf,application/pdf,.epub,.docx"
                uploadType="free-ebooks-files"
                currentUrl={form.file_url}
                onUpload={(url) => setForm({ ...form, file_url: url })}
              />
              <div className="mt-3">
                <label className="text-[10px] font-bold text-green-600 uppercase tracking-wider">Or Paste Download Link (Google Drive / Dropbox)</label>
                <input
                  data-testid="feb-file-url"
                  placeholder="https://drive.google.com/file/d/... or Dropbox link"
                  value={form.file_url}
                  onChange={(e) => setForm({ ...form, file_url: e.target.value })}
                  className={`${inputClass} border-green-300 focus:border-green-500`}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button data-testid="feb-submit" type="submit" disabled={!form.file_url} className="bg-[#059669] text-white font-bold py-3 px-6 text-sm transition-none disabled:opacity-40">
                {editing ? "Update" : "Add"} Free E-Book
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="admin-card border font-bold py-3 px-6 text-sm transition-none admin-text">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Books List */}
        <div className="space-y-3" data-testid="free-ebooks-list">
          {books.map((book) => (
            <div key={book.id} className="admin-card border p-4 flex gap-4">
              <div className="w-14 aspect-[3/4] bg-slate-200 flex-shrink-0 overflow-hidden">
                {book.image_url ? <img src={book.image_url} alt="" className="w-full h-full object-cover" /> : (
                  <div className="w-full h-full bg-green-50 flex items-center justify-center"><Gift size={18} className="text-green-600" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold admin-text">{book.title}</h3>
                      <span className="bg-green-100 text-green-700 text-[10px] font-bold px-1.5 py-0.5">FREE</span>
                    </div>
                    <p className="text-[10px] admin-text-secondary">{book.author || "—"} &middot; {book.category} &middot; {book.file_format} &middot; {book.file_size || "—"}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleEdit(book)} className="p-1.5 admin-text-secondary"><Edit size={14} /></button>
                    <button onClick={() => handleDelete(book.id)} className="p-1.5 text-red-400"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="text-xs admin-text-secondary">{book.downloads || 0} downloads</span>
                  {book.file_url && (
                    <a href={book.file_url} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-green-600 flex items-center gap-1">
                      <Download size={10} /> Download Link
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
          {books.length === 0 && !showForm && <p className="text-sm admin-text-secondary py-4 text-center">No free e-books yet.</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
